/**
 * Utility functions for grouping sources
 */

import type { AggregateFetcherSource } from './types';

export interface SourceGroup {
  groupId: string;
  sourceIndexes: number[];
}

/**
 * Groups sources by their type (extracted from source ID)
 * @param sources - Array of fetcher sources
 * @returns Map of group ID to source indexes
 */
export function groupSourcesByType<R, E, K>(
  sources: AggregateFetcherSource<R, E, K>[]
): Map<string, number[]> {
  const groups = new Map<string, number[]>();

  sources.forEach((source, index) => {
    let sourceType = '';

    if (source.id.includes('-')) {
      const typedSourceId = source.id as SourceId;
      sourceType = extractSourceType(typedSourceId);
    }

    if (!groups.has(sourceType)) {
      groups.set(sourceType, []);
    }

    groups.get(sourceType)!.push(index);
  });

  return groups;
}

/**
 * Creates a universal grouping configuration
 * Includes 'all' group that contains all sources and type-specific groups
 * @param sources - Array of fetcher sources
 * @param groupByType - Whether to create type-specific groups
 * @returns Array of source groups
 */
export function createSourceGroups<R, E, K>(
  sources: AggregateFetcherSource<R, E, K>[],
  groupByType: boolean = false
): SourceGroup[] {
  const groups: SourceGroup[] = [];

  // Always include 'all' group with all sources
  const allIndexes = sources.map((_, index) => index);
  groups.push({
    groupId: 'all',
    sourceIndexes: allIndexes
  });

  if (groupByType) {
    // Add type-specific groups
    const typeGroups = groupSourcesByType(sources);
    typeGroups.forEach((sourceIndexes, groupId) => {
      groups.push({
        groupId,
        sourceIndexes
      });
    });
  } else {
    // Add individual source groups
    sources.forEach((source, index) => {
      groups.push({
        groupId: source.id,
        sourceIndexes: [index]
      });
    });
  }

  return groups;
}

type SourceType = string;
type SourceId = `${SourceType}-${string}`;
/**
 * Extracts source type from source ID
 * @param sourceId - Source ID in format "type-sourceId"
 * @returns Source type
 */
export function extractSourceType(sourceId: SourceId) {
  return sourceId.split('-')[0];
}
